import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { fetchConfig, fetchFilters } from '@/lib/api';
import Button from '@/components/Button/Button';
import WarningTriangleIcon from '@/assets/warning-triangle.svg?react';

// Shown when the filter options (or the config) could not be loaded.
// The page stays usable; only the affected sections are unavailable.
const FiltersLoadError = () => {
  const filtersQuery = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const configQuery = useQuery({
    queryKey: ['config'],
    queryFn: fetchConfig,
  });

  const failedQueries = [filtersQuery, configQuery].filter(
    (query) => query.isError && !query.data,
  );

  if (failedQueries.length === 0) {
    return null;
  }

  const isRetrying = failedQueries.some((query) => query.isFetching);

  return (
    <Root role="alert">
      <WarningTriangleIcon />
      <Message>
        <FormattedMessage
          id="filtersLoadError"
          defaultMessage="Some filters couldn't be loaded."
        />
      </Message>
      <RetryButton
        variant="text"
        onClick={() => failedQueries.forEach((query) => query.refetch())}
        disabled={isRetrying}
        aria-label="Retry loading filters"
      >
        <FormattedMessage id="retry" defaultMessage="Retry" />
      </RetryButton>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.radii.sm,
  border: `1px solid ${theme.palette.grey400}`,

  svg: {
    flexShrink: 0,
    width: 20,
    height: 20,
  },
}));

const Message = styled('div')({
  flexGrow: 1,
  fontSize: 14,
  lineHeight: '20px',
});

const RetryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.white,
  textDecoration: 'underline',
  minWidth: 0,
}));

export default FiltersLoadError;
